import React, { useCallback, useEffect, useState } from 'react';
import { requestService } from '../services/dataService';
import { formatDateTime, StatusPill, toLabel } from './DetailDashboard';
import styles from '../pages/assets/css/DetailDashboard.module.css';

/**
 * Requests whose worklist never reached Orthanc (pending/failed), with batch republish
 * and the state of the automatic 5-minute retry.
 */
const WorklistRepublishPanel = ({ canWrite, onRepublished }) => {
  const [data, setData] = useState({ total: 0, items: [] });
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await requestService.getRepublishCandidates();
      setLoadFailed(false);
      setData(result);
      setSelected((current) => current.filter((id) => result.items.some((item) => item.id === id)));
    } catch (err) {
      setLoadFailed(true);
      setError(err?.detail || 'Failed to load requests waiting for a worklist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const republish = async (options) => {
    setWorking(true);
    setError('');
    setMessage('');
    try {
      const result = await requestService.republishWorklists(options);
      const failures = result.results.filter((item) => !item.ok);
      setMessage(`Republished ${result.succeeded} of ${result.attempted} request(s).`);
      if (failures.length) {
        setError(failures.slice(0, 5).map((item) => `${item.internal_request_id || item.request_id}: ${item.error}`).join(' · '));
      }
      setSelected([]);
      await load();
      if (onRepublished) onRepublished();
    } catch (err) {
      setError(err?.detail || 'Republishing failed');
    } finally {
      setWorking(false);
    }
  };

  const toggle = (id) => setSelected((current) => (
    current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
  ));
  const allSelected = data.items.length > 0 && selected.length === data.items.length;

  if (loading && !data.items.length) return null;

  return (
    <section className={styles.card}>
      <div className={styles.cardHeader}>
        <h2>
          Worklist Publishing
          {' '}
          {loadFailed ? (
            <StatusPill value="unknown" label="Status unavailable" />
          ) : (
            <StatusPill
              value={data.total ? 'failed' : 'active'}
              label={data.total ? `${data.total} waiting` : 'All published'}
            />
          )}
        </h2>
        {canWrite && data.total ? (
          <div className={styles.buttonRow}>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={working || !selected.length}
              onClick={() => republish({ requestIds: selected })}
            >
              Republish selected ({selected.length})
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={working}
              onClick={() => republish({ allCandidates: true })}
            >
              {working ? 'Republishing…' : `Republish all (${data.total})`}
            </button>
          </div>
        ) : null}
      </div>

      <p className={styles.muted}>
        Pending or failed requests whose worklist is not in Orthanc, so the machine cannot see them.
        {data.auto_retry_enabled
          ? ` The gateway retries these automatically every 5 minutes (up to ${data.auto_retry_max_attempts} times, for requests under ${data.auto_retry_max_age_hours} hours old).`
          : ' Automatic retry is switched off.'}
      </p>
      {message ? <div className={styles.success}>{message}</div> : null}
      {error ? <div className={styles.error}>{error}</div> : null}

      {data.total ? (
        <>
          <button type="button" className={styles.linkButton} onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Hide requests' : `Show ${data.total} request(s)`}
          </button>
          {expanded ? (
            <div className={`${styles.tableWrap} ${styles.tableFramed}`}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {canWrite ? (
                      <th>
                        <input
                          type="checkbox"
                          aria-label="Select all"
                          checked={allSelected}
                          onChange={() => setSelected(allSelected ? [] : data.items.map((item) => item.id))}
                        />
                      </th>
                    ) : null}
                    <th>Request</th>
                    <th>Patient</th>
                    <th>Facility</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Automatic retry</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr key={item.id}>
                      {canWrite ? (
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${item.internal_request_id}`}
                            checked={selected.includes(item.id)}
                            onChange={() => toggle(item.id)}
                          />
                        </td>
                      ) : null}
                      <td>
                        <code>{item.internal_request_id}</code>
                        <span className={styles.subText}>{item.emr_request_id}</span>
                      </td>
                      <td>{item.patient_id}</td>
                      <td>{item.facility_id}</td>
                      <td>
                        <StatusPill value={item.status === 'failed' ? 'failed' : 'pending'} label={toLabel(item.status)} />
                        {item.status_message ? <span className={styles.subText}>{item.status_message}</span> : null}
                      </td>
                      <td className={styles.nowrap}>{formatDateTime(item.created_at)}</td>
                      <td className={styles.matchCell}>
                        {item.auto_retry ? (
                          <>
                            {item.auto_retry.attempts}/{data.auto_retry_max_attempts} attempts
                            <span className={styles.subText}>last {formatDateTime(item.auto_retry.last_attempt_at)}</span>
                            {item.auto_retry.last_error ? <span className={styles.subText}>{item.auto_retry.last_error}</span> : null}
                          </>
                        ) : <span className={styles.muted}>Not tried yet</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export default WorklistRepublishPanel;
